import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  LogOut, Moon, Sun, Plus, FileText, Trash2, Printer, Clock, 
  Users, UserCog, Search, FolderOpen, Download, BarChart3, Receipt
} from 'lucide-react';
import CreateWorkCardModal from '../components/CreateWorkCardModal';
import WorkCardDetailModal from '../components/WorkCardDetailModal';
import UserManagementModal from '../components/UserManagementModal';
import CustomerManagementModal from '../components/CustomerManagementModal';
import ReportsPage from '../components/ReportsPage';
import InvoiceModal from '../components/InvoiceModal';
import InvoiceListModal from '../components/InvoiceListModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, logout, isAdmin } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [workcards, setWorkcards] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('workcards');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showInvoiceListModal, setShowInvoiceListModal] = useState(false);
  const [selectedWorkcard, setSelectedWorkcard] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [workcardsRes, customersRes] = await Promise.all([
        axios.get(`${API}/workcards`),
        axios.get(`${API}/customers`)
      ]);
      setWorkcards(workcardsRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      toast.error('Kunne ikke hente data');
    } finally {
      setLoading(false);
    }
  };

  const handleClockAction = async (workcard) => {
    const isClocked = workcard.active_clock_ins?.some(c => c.user_id === user.id);
    
    try {
      if (isClocked) {
        await axios.post(`${API}/workcards/${workcard.id}/clock-out`);
        toast.success('Stemplet ud!');
      } else {
        await axios.post(`${API}/workcards/${workcard.id}/clock-in`);
        toast.success('Stemplet ind!');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Handling fejlede');
    }
  };

  const handleDelete = async (workcard) => {
    if (!window.confirm(`Er du sikker på du vil slette arbejdskort #${workcard.card_number}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/workcards/${workcard.id}`);
      toast.success('Arbejdskort slettet');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette');
    }
  };

  const handlePrint = (workcard) => {
    setSelectedWorkcard(workcard);
    setShowDetailModal(true);
    setTimeout(() => {
      window.print();
    }, 500);
  };

  const handleExportTxt = async (workcard) => {
    try {
      const response = await axios.get(`${API}/workcards/${workcard.id}/export/txt`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arbejdskort_${workcard.card_number}.txt`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('TXT fil downloadet');
    } catch (error) {
      toast.error('Kunne ikke eksportere');
    }
  };

  const handleExportPdf = async (workcard) => {
    try {
      const response = await axios.get(`${API}/workcards/${workcard.id}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data, { type: 'application/pdf' }]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `arbejdskort_${workcard.card_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF fil downloadet');
    } catch (error) {
      toast.error('Kunne ikke eksportere PDF');
    }
  };

  const handleCreateInvoice = (workcard) => {
    setSelectedWorkcard(workcard);
    setShowInvoiceModal(true);
  };

  const filteredWorkcards = workcards.filter(wc => 
    wc.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    wc.card_number.includes(searchQuery) ||
    wc.task_description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('da-DK', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-card border-b no-print">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <h1 className="text-2xl font-bold tracking-tight">Arbejdskort System</h1>
              <Badge variant="outline" className="hidden sm:flex">
                {user?.role === 'admin' ? 'Administrator' : 'Bruger'}
              </Badge>
            </div>
            
            <div className="flex items-center gap-2">
              {isAdmin() && (
                <>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowCustomerModal(true)}
                    data-testid="manage-customers-btn"
                  >
                    <Users className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Kunder</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowUserModal(true)}
                    data-testid="manage-users-btn"
                  >
                    <UserCog className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Brugere</span>
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowInvoiceListModal(true)}
                    data-testid="view-invoices-btn"
                  >
                    <Receipt className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Fakturaer</span>
                  </Button>
                </>
              )}
              <Button
                variant="ghost"
                size="icon"
                onClick={toggleTheme}
                className="theme-toggle"
                data-testid="dashboard-theme-toggle"
              >
                {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={logout}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Log ud
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="no-print">
          <TabsList className="mb-6">
            <TabsTrigger value="workcards" data-testid="workcards-tab">
              <FileText className="h-4 w-4 mr-2" />
              Arbejdskort
            </TabsTrigger>
            <TabsTrigger value="reports" data-testid="reports-tab">
              <BarChart3 className="h-4 w-4 mr-2" />
              Rapporter
            </TabsTrigger>
          </TabsList>

          <TabsContent value="workcards">
            {/* Actions Bar */}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Søg efter kunde, nummer eller opgave..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={() => setShowCreateModal(true)}
                className="shrink-0"
                data-testid="create-workcard-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Opret Arbejdskort
              </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{workcards.length}</div>
                  <p className="text-sm text-muted-foreground">Arbejdskort</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold">{customers.length}</div>
                  <p className="text-sm text-muted-foreground">Kunder</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-green-500">
                    {workcards.filter(wc => wc.active_clock_ins?.some(c => c.user_id === user?.id)).length}
                  </div>
                  <p className="text-sm text-muted-foreground">Du er stemplet ind</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <div className="text-2xl font-bold text-blue-500">
                    {workcards.reduce((sum, wc) => sum + (wc.active_clock_ins?.length || 0), 0)}
                  </div>
                  <p className="text-sm text-muted-foreground">Aktive stempler</p>
                </CardContent>
              </Card>
            </div>

            {/* Work Cards List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Arbejdskort ({filteredWorkcards.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
                  </div>
                ) : filteredWorkcards.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p>Ingen arbejdskort fundet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {filteredWorkcards.map((workcard) => {
                      const isUserClocked = workcard.active_clock_ins?.some(c => c.user_id === user?.id);
                      
                      return (
                        <div
                          key={workcard.id}
                          className="workcard-item flex flex-col sm:flex-row sm:items-center justify-between p-4 rounded-lg border bg-card hover:shadow-md gap-4"
                          data-testid={`workcard-item-${workcard.card_number}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="font-mono text-sm text-muted-foreground">#{workcard.card_number}</span>
                              <h3 className="font-semibold truncate">{workcard.customer_name}</h3>
                              {workcard.active_clock_ins?.length > 0 && (
                                <Badge variant="secondary" className="text-xs">
                                  {workcard.active_clock_ins.length} aktiv(e)
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground truncate mb-1">
                              {workcard.task_description}
                            </p>
                            <div className="flex gap-4 text-xs text-muted-foreground">
                              <span>Oprettet: {formatDate(workcard.created_at)}</span>
                              <span>Opdateret: {formatDate(workcard.updated_at)}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            {/* Clock In/Out Button */}
                            <Button
                              onClick={() => handleClockAction(workcard)}
                              className={`clock-btn min-w-[120px] ${
                                isUserClocked 
                                  ? 'bg-red-600 hover:bg-red-700 text-white' 
                                  : 'bg-green-600 hover:bg-green-700 text-white'
                              }`}
                              data-testid={`clock-btn-${workcard.card_number}`}
                            >
                              <Clock className="h-4 w-4 mr-2" />
                              {isUserClocked ? 'Stempl Ud' : 'Stempl Ind'}
                            </Button>
                            
                            {/* Invoice Button */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleCreateInvoice(workcard)}
                              title="Opret Faktura"
                              data-testid={`invoice-btn-${workcard.card_number}`}
                            >
                              <Receipt className="h-4 w-4" />
                            </Button>
                            
                            {/* Open Button */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => {
                                setSelectedWorkcard(workcard);
                                setShowDetailModal(true);
                              }}
                              data-testid={`open-btn-${workcard.card_number}`}
                            >
                              <FolderOpen className="h-4 w-4" />
                            </Button>
                            
                            {/* Print Button */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handlePrint(workcard)}
                              data-testid={`print-btn-${workcard.card_number}`}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            
                            {/* Export PDF */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleExportPdf(workcard)}
                              title="Download PDF"
                              data-testid={`export-pdf-btn-${workcard.card_number}`}
                            >
                              <FileText className="h-4 w-4" />
                            </Button>
                            
                            {/* Export TXT */}
                            <Button
                              variant="outline"
                              size="icon"
                              onClick={() => handleExportTxt(workcard)}
                              title="Download TXT"
                              data-testid={`export-btn-${workcard.card_number}`}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                            
                            {/* Delete Button (Admin only) */}
                            {isAdmin() && (
                              <Button
                                variant="destructive"
                                size="icon"
                                onClick={() => handleDelete(workcard)}
                                data-testid={`delete-btn-${workcard.card_number}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="reports">
            <ReportsPage />
          </TabsContent>
        </Tabs>
      </main>

      {/* Modals */}
      <CreateWorkCardModal
        open={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        customers={customers}
        onSuccess={() => {
          fetchData();
          setShowCreateModal(false);
        }}
      />
      
      <WorkCardDetailModal
        open={showDetailModal}
        onClose={() => setShowDetailModal(false)}
        workcard={selectedWorkcard}
        onUpdate={fetchData}
      />
      
      <UserManagementModal
        open={showUserModal}
        onClose={() => setShowUserModal(false)}
      />
      
      <CustomerManagementModal
        open={showCustomerModal}
        onClose={() => setShowCustomerModal(false)}
        onSuccess={fetchData}
      />

      <InvoiceModal
        open={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        workcard={selectedWorkcard}
        onSuccess={() => {
          setShowInvoiceModal(false);
          toast.success('Faktura oprettet!');
        }}
      />

      <InvoiceListModal
        open={showInvoiceListModal}
        onClose={() => setShowInvoiceListModal(false)}
      />
    </div>
  );
};

export default DashboardPage;
